import { FC } from 'react';

import { Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import FlatButton from '@/components/flat-button';
import CheckboxBase from '../checkbox/base';
import { FormProps } from '../helpers';
import { UiOption } from '@/lib/helpers';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    maxHeight: 500,
    overflowY: 'auto',
    paddingRight: 12,
  },
  itemRow: {
    '& .MuiFormControlLabel-label': {
      fontSize: theme.typography.body2.fontSize,
    },
    '&:last-child': {
      marginBottom: 0,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderRadius: theme.shape.borderRadius,
    },
    '&:hover $onlyButton': {
      opacity: 1,
    },
  },
  item: {
    flex: 1,
    minWidth: 0,
  },
  onlyButton: {
    opacity: 0,
    padding: '0px 6px',
  },
}));

interface Props extends Pick<FormProps, 'name' | 'disabled' | 'size'> {
  options: UiOption[];
  value: string[];
  onChange: (selectedValues: string[]) => void;
}

const CheckboxMulti: FC<Props> = ({
  name,
  options,
  value,
  onChange,
  disabled = false,
  size = 'small',
}) => {
  const styles = useStyles();

  const handleChange = (optionValue: string, checked: boolean) => {
    const updatedValues = checked
      ? [...value, optionValue]
      : value.filter((val) => val !== optionValue);
    onChange(updatedValues);
  };

  const handleSelectOnly = (optionValue: string) => {
    onChange([optionValue]);
  };
  return (
    <Stack className={styles.root} role="group" aria-label={name}>
      {options.map(({ value: optionValue, label }) => {
        const isChecked = value.includes(optionValue);
        const inputName = `${name}-${optionValue}`;
        return (
          <Stack
            key={optionValue}
            direction="row"
            justifyContent="space-between"
            pl={1}
            m={0}
            className={styles.itemRow}
          >
            <CheckboxBase
              name={inputName}
              checked={isChecked}
              onChange={(checked: boolean) => handleChange(optionValue, checked)}
              label={label}
              size={size}
              disabled={disabled}
            />
            <FlatButton
              className={styles.onlyButton}
              onClick={() => handleSelectOnly(optionValue)}
              label="Only"
              size="x-small"
              color="link"
            />
          </Stack>
        );
      })}
    </Stack>
  );
};

export default CheckboxMulti;
